/** Produktion intern (Freundebuch: Cover-Eingang, Binden) und Partner-Sendungen (Tracking nachtragen). */
import { useRef, useState, type FormEvent } from 'react';
import { Link } from 'react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { partnerTracking } from '../api/auftraege';
import { partnerCsvImport, produktion, produktionScan } from '../api/lager';
import { datum } from '../components/format';
import { SendungBadge } from '../components/Status';
import { ton } from '../components/ton';
import { Darf, Dialog, DialogFormular, Feld, Karte, Seitenkopf, Tabelle, useToast, Zustand } from '../components/ui';
import type { PartnerSendung } from '../api/typen';

const PS_TEXT: Record<string, string> = { offen: 'Cover offen', uebergeben: 'bei B&B', eingegangen: 'Cover da', fertig: 'gebunden' };

export function ProduktionSeite() {
  const qc = useQueryClient();
  const toast = useToast();
  const q = useQuery({ queryKey: ['produktion'], queryFn: produktion });
  const [code, setCode] = useState('');
  const [meldung, setMeldung] = useState<{ text: string; ok: boolean } | null>(null);
  const [partner, setPartner] = useState<PartnerSendung | null>(null);
  const feld = useRef<HTMLInputElement>(null);

  async function scannen(e: FormEvent) {
    e.preventDefault();
    const c = code.trim(); setCode('');
    if (!c) return;
    try { const r = await produktionScan(c); setMeldung({ text: r.meldung, ok: true }); ton('ok'); void qc.invalidateQueries({ queryKey: ['produktion'] }); }
    catch (err) { setMeldung({ text: (err as Error).message, ok: false }); ton('fehler'); }
    feld.current?.focus();
  }
  const ueberfaellig = q.data?.intern.filter((z) => z.ueberfaellig).length ?? 0;
  return (
    <section>
      <Seitenkopf titel="Produktion intern" unter="B&B-Laufzettel scannen (LZ-<Bestellnummer>-<Position>, bei Menge > 1 je Stück LZ-…-<Position>-<Stück>): 1. Scan = Cover eingegangen, 2. Scan = gebunden (fertig). Alle Stücke gebunden → Sendung wird bereit." />
      <form className="scanzeile" onSubmit={scannen}>
        <input ref={feld} autoFocus value={code} onChange={(e) => setCode(e.target.value)} placeholder="Laufzettel scannen (LZ-…)" aria-label="Laufzettel" data-testid="produktion-scan" />
        <button type="submit" className="knopf">Buchen</button>
        {meldung && <span className={`meldung ${meldung.ok ? 'ok' : 'fehler'}`} role="status">{meldung.text}</span>}
      </form>
      <Zustand laden={q.isLoading} fehler={q.error}>
        {q.data && (
          <div className="raster-2">
            <Karte titel={`Freundebuch personalisiert (${q.data.intern.length}${ueberfaellig ? `, ${ueberfaellig} überfällig` : ''})`}>
              <Tabelle zeilen={q.data.intern} schluessel={(z) => `${z.order_item_id}:${z.stueck ?? 1}`} leer="Nichts in Produktion"
                spalten={[
                  { kopf: 'Laufzettel', zelle: (z) => z.laufzettel, klasse: 'mono' },
                  { kopf: 'Auftrag', zelle: (z) => <Link to={`/auftraege/${z.order_id}`} className="mono">{z.bestellnummer}</Link> },
                  { kopf: 'Motiv', zelle: (z) => z.variante ?? z.name },
                  { kopf: 'Stand', zelle: (z) => <span className={`badge ps-${z.produktions_status}`}>{PS_TEXT[z.produktions_status] ?? z.produktions_status}</span> },
                  { kopf: 'Bezahlt', zelle: (z) => datum(z.bezahlt_at) },
                  { kopf: 'WT', zelle: (z) => <span className={z.ueberfaellig ? 'warn-text' : ''} title={z.ueberfaellig ? 'überfällig (mehr als 3 Werktage)' : undefined}>{z.werktage}{z.ueberfaellig ? ' !' : ''}</span>, rechts: true },
                ]} />
            </Karte>
            <Karte titel={`Partner-Sendungen (${q.data.partner.length})`}>
              <Tabelle zeilen={q.data.partner} schluessel={(z) => z.sendung_id} leer="Keine offenen Partner-Sendungen"
                spalten={[
                  { kopf: 'Sendung', zelle: (z) => <Link to={`/auftraege/${z.order_id}`} className="mono">{z.kennung}</Link> },
                  { kopf: 'Positionen', zelle: (z) => <span className="kuerzen" style={{ maxWidth: '10rem' }} title={z.positionen.join(', ')}>{z.positionen.join(', ')}</span> },
                  { kopf: 'Status', zelle: (z) => <SendungBadge status={z.status} /> },
                  { kopf: 'WT', zelle: (z) => <span title={`bezahlt ${datum(z.bezahlt_at)}`}>{z.werktage}</span>, rechts: true },
                  { kopf: '', zelle: (z) => <button type="button" className="knopf-zweit klein" onClick={() => setPartner(z)}>Tracking</button> },
                ]} />
              <p className="hinweis">Der Partner meldet Sendungsnummern automatisch (Webhook). „Tracking“ ist der Rückfall von Hand.</p>
              <Darf rolle="leitung"><CsvImport onFertig={() => void qc.invalidateQueries()} /></Darf>
            </Karte>
          </div>
        )}
      </Zustand>
      <Dialog offen={!!partner} titel={`Tracking für ${partner?.bestellnummer ?? ''}`} onSchliessen={() => setPartner(null)}>
        {partner && <TrackingFormular sid={partner.sendung_id} onAbbrechen={() => setPartner(null)} onFertig={() => { setPartner(null); toast('Tracking gespeichert, Mail an den Kunden ausgelöst'); void qc.invalidateQueries(); }} />}
      </Dialog>
    </section>
  );
}

function TrackingFormular({ sid, onAbbrechen, onFertig }: { sid: string; onAbbrechen: () => void; onFertig: () => void }) {
  const [nr, setNr] = useState('');
  const [carrier, setCarrier] = useState('dhl');
  return (
    <DialogFormular absendenText="Speichern" onAbbrechen={onAbbrechen} onAbsenden={async () => { await partnerTracking(sid, carrier, nr.trim()); onFertig(); }}>
      <Feld label="Versanddienst"><select value={carrier} onChange={(e) => setCarrier(e.target.value)}><option value="dhl">DHL</option><option value="dpd">DPD</option><option value="gls">GLS</option><option value="ups">UPS</option></select></Feld>
      <Feld label="Sendungsnummer"><input autoFocus value={nr} onChange={(e) => setNr(e.target.value)} data-testid="partner-tracking" /></Feld>
    </DialogFormular>
  );
}

/** Datei-Import der Partner-Sendungsnummern (R42): CSV kennung;tracking;carrier. */
function CsvImport({ onFertig }: { onFertig: () => void }) {
  const toast = useToast();
  async function lade(datei: File | undefined) {
    if (!datei) return;
    try {
      const r = await partnerCsvImport(await datei.text());
      toast(`CSV: ${r.gemeldet} gemeldet, ${r.doppelt} schon bekannt${r.fehler.length ? `, ${r.fehler.length} Fehler (Zeile ${r.fehler.map((f) => f.zeile).join(', ')})` : ''}`, r.fehler.length ? 'fehler' : undefined);
      onFertig();
    } catch (e) { toast((e as Error).message, 'fehler'); }
  }
  return (
    <label className="knopf-zweit klein" style={{ display: 'inline-block', marginTop: '0.4rem' }}>
      CSV importieren (kennung;tracking;carrier)
      <input type="file" accept=".csv,text/csv,text/plain" hidden data-testid="partner-csv" onChange={(e) => { void lade(e.target.files?.[0]); e.target.value = ''; }} />
    </label>
  );
}
